import React from 'react';
import { RC_PINE, RC_BRASS, RC_IVORY, RC_INK, RC_TEXT } from '../lib/brand';

/**
 * Real Content. Brand mark + wordmark.
 *
 * 2026-09 rebrand ("Haus & Play", picked from the logo canvas):
 *   - Mark: a quiet house outline (roof + walls, rounded joins) with a
 *     brass play triangle inside — Immobilie + Video in one shape.
 *   - Wordmark: "Real Content" in Fraunces (the brand serif, loaded
 *     app-wide via index.css) with a brass dot separator — the dot is
 *     the one carry-over from the old logo.
 *   - No gradients, no glow. Two tones instead:
 *       tone="ink" (default)  → pine mark stroke + ink text, for the
 *                               light Makler-Premium surfaces (the
 *                               whole app since the full re-theme)
 *       tone="light"          → ivory mark stroke + near-white text,
 *                               for dark or photo surfaces
 *
 * Source of truth: /public/logos/USAGE.md. When you change colour or
 * geometry here, propagate to the standalone assets under /public
 * (favicon.svg, favicon-*.png, apple-touch-icon.png, og-image.png,
 * logos/exports/) — those are used by browsers, emails and social
 * previews and are generated, not hand-drawn.
 *
 *   <RCLogo size="md" />               // dark surfaces (app nav)
 *   <RCLogo size="md" tone="ink" />    // light surfaces (landing)
 *   <RCLogoMark size={48} />           // mark only, custom px size
 *   <RCWordmark size={28} />           // wordmark only
 *   <RCLogo monochrome />              // single-colour variant
 *
 * Don't reimplement the logo inline. Always import.
 */

/* ---------- Mark (house + play) ---------- */
// `glow` is accepted for old-callsite compatibility and ignored — the
// violet halo died with the old identity.
export function RCLogoMark({ size = 64, tone = 'ink', glow, className }) {
  const stroke = tone === 'ink' ? RC_PINE : RC_IVORY;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="Real Content"
      className={className}
    >
      <path
        d="M 18 46 L 50 20 L 82 46 L 82 80 L 18 80 Z"
        fill="none"
        stroke={stroke}
        strokeWidth="8"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      <path d="M 43 50 L 43 70 L 61 60 Z" fill={RC_BRASS} />
    </svg>
  );
}

/* ---------- Wordmark ---------- */
// `tone`: 'light' (default) for dark surfaces, 'ink' for light ones.
// `monochrome` renders dot and text in one colour (that tone's text
// colour) for single-colour contexts.
export function RCWordmark({ size = 28, monochrome = false, tone = 'ink', className }) {
  const text = tone === 'ink' ? RC_INK : RC_TEXT;
  const baseStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: size * 0.26,
    fontFamily: "'Fraunces', Georgia, 'Times New Roman', serif",
    fontWeight: 700,
    fontSize: size,
    letterSpacing: '-0.01em',
    lineHeight: 1,
  };
  const dotStyle = {
    width: size * 0.2,
    height: size * 0.2,
    borderRadius: '50%',
    background: monochrome ? text : RC_BRASS,
    flexShrink: 0,
  };
  return (
    <span style={baseStyle} className={className}>
      <span style={{ color: text }}>Real</span>
      <span style={dotStyle} aria-hidden="true" />
      <span style={{ color: text }}>Content</span>
    </span>
  );
}

/* ---------- Lockup (mark + wordmark) ---------- */
// Mark height ≈ 1.5x text size; gap is tight so mark and 'R' feel like one unit.
const LOCKUP_DIMS = {
  lg: { mark: 44, txt: 32, gap: 10 },
  md: { mark: 32, txt: 22, gap: 8 },
  sm: { mark: 22, txt: 16, gap: 6 },
  xs: { mark: 18, txt: 14, gap: 5 },
};

export default function RCLogo({ size = 'md', monochrome = false, tone = 'ink', className, glow }) {
  const dims = LOCKUP_DIMS[size] || LOCKUP_DIMS.md;
  return (
    <div
      className={`rc-logo ${className || ''}`}
      style={{ display: 'inline-flex', alignItems: 'center', gap: dims.gap }}
    >
      <RCLogoMark size={dims.mark} tone={tone} />
      <RCWordmark size={dims.txt} monochrome={monochrome} tone={tone} />
    </div>
  );
}
